import { Link } from "react-router-dom";
import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Plane, GraduationCap, Heart, ArrowRight } from "lucide-react";

const Marketplace = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const marketplaces = [
    {
      id: "travel",
      title: "Flex Travel",
      description: "Book flights, hotels, and vacation packages with flexible payment options. Split your travel costs into manageable monthly payments.",
      icon: Plane,
      route: "/travel",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-600",
      features: ["Flight bookings", "Hotel reservations", "Vacation packages", "Travel insurance"]
    },
    {
      id: "education",
      title: "Flex Education",
      description: "Invest in your future with flexible payment plans for courses, degrees, and educational programs from top institutions.",
      icon: GraduationCap,
      route: "/education",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-600",
      features: ["University programs", "Professional courses", "Certification programs", "Skills training"]
    },
    {
      id: "healthcare",
      title: "Flex Healthcare",
      description: "Access quality healthcare services with affordable payment plans. From routine checkups to specialized treatments.",
      icon: Heart,
      route: "/healthcare",
      gradient: "from-red-500/20 to-pink-500/20",
      iconColor: "text-red-600",
      features: ["Medical treatments", "Dental care", "Cosmetic procedures", "Wellness programs"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/10">
      <Navigation />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
              Choose Your <span className="gradient-text">Flex</span>
            </h1>
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-6 max-w-4xl mx-auto border border-primary/20">
              <p className="text-lg text-muted-foreground">
                Explore our specialized marketplaces designed for your unique needs. 
                Each offers flexible payment solutions tailored to different life moments.
              </p>
            </div>
          </div>

          {/* Marketplace Options */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {marketplaces.map((marketplace) => {
              const IconComponent = marketplace.icon;
              return (
                <Link 
                  key={marketplace.id}
                  to={marketplace.route}
                  className="group block"
                >
                  <div className={`bg-gradient-to-br ${marketplace.gradient} p-8 rounded-3xl border border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]`}>
                    <div className="flex items-center justify-between mb-6">
                      <div className={`w-16 h-16 bg-background/60 rounded-2xl flex items-center justify-center border border-primary/10`}>
                        <IconComponent className={`w-8 h-8 ${marketplace.iconColor}`} />
                      </div>
                      <ArrowRight className="w-6 h-6 text-primary/60 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                    
                    <h2 className="text-2xl font-heading font-bold mb-4 group-hover:text-primary transition-colors">
                      {marketplace.title}
                    </h2>
                    
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {marketplace.description}
                    </p>
                    
                    <div className="space-y-2">
                      {marketplace.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-primary/10">
                      <span className="text-sm font-medium text-primary group-hover:underline">
                        Explore {marketplace.title} →
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Why Choose Flexaura Section */}
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-3xl p-8 md:p-12 border border-primary/20">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Why Choose <span className="gradient-text">Flexaura</span>?
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                We're the first marketplace to bring together BNPL providers, banks, and finance houses 
                under one roof, giving you the most flexible payment options available.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-background/60 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary/10">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
                <h3 className="font-heading font-semibold mb-2">Multiple Options</h3>
                <p className="text-sm text-muted-foreground">
                  Compare BNPL, bank installments, and traditional financing in one place
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-background/60 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary/10">
                  <div className="w-3 h-3 bg-accent rounded-full"></div>
                </div>
                <h3 className="font-heading font-semibold mb-2">Best Rates</h3>
                <p className="text-sm text-muted-foreground">
                  Our marketplace ensures you get the most competitive rates and terms
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-background/60 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary/10">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                </div>
                <h3 className="font-heading font-semibold mb-2">Instant Approval</h3>
                <p className="text-sm text-muted-foreground">
                  Get pre-qualified instantly and know your options before you shop
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Marketplace;